import { FormPayload } from "@/app/interface/components";
import { sendToSyonet } from "@/app/services/syonetIntegration";
import { pushLeadForm } from "@/app/utils/gtm";

export type TeamType =
	| "compre meu carro"
	| "venda direta"
	| "vende meu carro"
	| "serviços"
	| "cms"
	| "veiculos-novos"
	| "home"
	| "consorcio"
	| "Veiculos Novos";

interface FormData {
	name: string;
	cpf: string;
	email: string;
	phone: string;
	dealer?: string;
	model?: string;
	service?: string;
	page?: string;
	contactMethod?: string;
	plate?: string;
	mileage?: string;
	expectedValue?: string;
}

interface SubmitParams {
	params: string;
	consorcio: boolean;
	registrationNumber: string;
	team: TeamType;
	locationId: number;
	model: string | number;
	brand?: string;
	eventType?: string;
	page: string;
	wpp: boolean;
	comment?: string;
	successRedirectUrl?: string;
}

const URL_TO_MODEL_MAP: { [key: string]: string } = {
	"/corolla-cross": "Corolla Cross",
	"/corolla-cross-gr-sport": "Corolla Cross",
	"/corolla": "Corolla",
	"/corolla-hatch": "Corolla Hatch",
	"/corolla-gr-sport": "Corolla",
	"/hilux": "Hilux",
	"/hilux-gr-sport": "Hilux",
	"/rav4": "RAV4",
	"/sw4": "SW4",
	"/hiace": "Hiace",
	"/move-brasil": "Yaris Cross XR - MOVE BRASIL",
	"/yaris-cross": "Yaris Cross - Landing Page",
	"/lp-yaris-cross": "PRE-VENDA YARIS CROSS",
};

export function normalizeModelName(modelName: string): string {
	if (
		!modelName ||
		modelName === "Não informado" ||
		modelName.trim() === ""
	) {
		return "Não informado";
	}

	const modelLower = modelName.toLowerCase();
	let cleanModel = modelName;

	cleanModel = cleanModel.replace(/\s*-\s*\d{4}\/\d{4}/, "");
	cleanModel = cleanModel.replace(/\s*\(\d{4}\/\d{4}\)/, "");
	cleanModel = cleanModel.replace(/\s*\d{4}\/\d{4}/, "");

	if (modelLower.includes("corolla")) {
		if (modelLower.includes("cross")) {
			return "Corolla Cross";
		}
		if (modelLower.includes("hatch")) {
			return "Corolla Hatch";
		}
		return "Corolla";
	}

	if (modelLower.includes("hilux")) {
		return "Hilux";
	}

	if (modelLower.includes("rav4") || modelLower.includes("rav-4")) {
		return "RAV4";
	}

	if (modelLower.includes("sw4") || modelLower.includes("sw-4")) {
		return "SW4";
	}

	if (modelLower.includes("hiace")) {
		if (modelLower.includes("tp hiace") || modelLower === "tp hiace") {
			return "TP Hiace";
		}

		return "Hiace";
	}

	if (modelLower.includes("yaris") && modelLower.includes("cross")) {
		return "Yaris Cross";
	}

	if (modelLower.includes("bz4x") || modelLower.includes("bz 4x")) {
		return "bZ4X";
	}

	return cleanModel.trim();
}

function resolveSyonetComment(
	normalizedModel: string,
	page?: string
): string | undefined {
	const pageLower = (page || "").toLowerCase();

	// LP pré-lançamento Yaris Cross
	if (
		normalizedModel === "Yaris Cross" ||
		(pageLower.includes("yaris") && pageLower.includes("cross"))
	) {
		return "LP Pré-lançamento-YARIS_CROSS";
	}

	// TP Hiace
	if (normalizedModel === "TP Hiace" || pageLower.includes("tp hiace")) {
		return "TP Hiace TouchPoint";
	}

	// Black Week 0km
	if (page === "[0 Km] Black Week") {
		return "[0 Km] Black Week";
	}

	return undefined; // sem comment específico
}

export function detectModelFromPage(): string {
	if (typeof window !== "undefined") {
		const pathname = window.location.pathname;
		const modelFromUrl = URL_TO_MODEL_MAP[pathname];
		if (modelFromUrl) {
			return modelFromUrl;
		}
	}

	return "Não informado";
}

export function determineTeamBasedOnPage(page: string): string {
	const pageLower = page?.toLowerCase() || "";
	if (pageLower.includes("venda-direta")) return "Venda Direta";
	if (pageLower.includes("serviços - pós-venda"))
		return "Serviços - Pós-Venda";
	if (pageLower.includes("consorcio")) return "Consórcio";
	if (pageLower.includes("pessoa-com-deficiencia"))
		return "Venda Direta - PCD";
	if (pageLower.includes("frotista")) return "Venda Direta - Frotista";
	if (pageLower.includes("cnpj")) return "Venda Direta - CNPJ";
	if (pageLower.includes("governo")) return "Venda Direta - Governo";
	if (pageLower.includes("taxista")) return "Venda Direta - Taxista";
	if (pageLower.includes("produtor-rural"))
		return "Venda Direta - Produtor Rural";
	if (
		[
			"bz4x",
			"corolla-cross",
			"corolla-hatch",
			"corolla",
			"hilux",
			"rav4",
			"sw4",
			"hiace",
		].some((car) => pageLower.includes(car))
	) {
		return "Veículos Novos";
	}
	if (pageLower.includes("servicos")) return "Serviços";
	if (pageLower.includes("cms")) return "cms";
	if (pageLower.includes("[carro]")) return "veiculos-novos";
	if (pageLower.includes("app")) return "home";
	if (pageLower.includes("não informado")) return "não informado";

	return "Não Informado";
}

const handleSubmit = async (
	event: React.FormEvent<HTMLFormElement>,
	submitParams: SubmitParams
) => {
	event.preventDefault();

	const form = event.currentTarget;
	const submitButton = form.querySelector(
		'button[type="submit"]'
	) as HTMLButtonElement;

	if (submitButton) {
		submitButton.disabled = true;
		submitButton.style.opacity = "0.7";
		submitButton.style.cursor = "not-allowed";
	}

	const brand = (submitParams.brand ?? "toyota").toLowerCase();

	const getFieldValue = (fieldId: string): string => {
		const element = form.querySelector<
			HTMLInputElement | HTMLSelectElement
		>(`#${fieldId}`);
		return element?.value.trim() ?? "";
	};

	const getContactMethods = (): string => {
		const checkboxes = form.querySelectorAll<HTMLInputElement>(
			'input[name="contactMethod"]:checked'
		);
		return Array.from(checkboxes)
			.map((input) => input.value)
			.join(", ");
	};

	const currentPageTeam = determineTeamBasedOnPage(submitParams.page);

	const formData: FormData = {
		name: getFieldValue("nameInput"),
		cpf: getFieldValue("cpfInput").replace(/\D/g, ""),
		email: getFieldValue("emailInput"),
		phone: getFieldValue("phoneInput").replace(/\D/g, ""),
		dealer: getFieldValue("dealerInput"),
		model: getFieldValue("modelInput"),
		service: getFieldValue("serviceInput"),
		contactMethod: getContactMethods(),
		plate: getFieldValue("plateInput")?.toUpperCase(),
		mileage: getFieldValue("mileageInput"),
		expectedValue: getFieldValue("expectedValueInput"),
		page: submitParams.page,
	};

	try {
		const response = await fetch("/api/submitForm", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"x-api-key": process.env.NEXT_PUBLIC_API_KEY || "",
			},
			body: JSON.stringify({
				...formData,
				...submitParams,
				model: formData.model,
				team: currentPageTeam,
				brand,
			}),
		});

		if (!response.ok) {
			throw new Error("Erro ao enviar os dados para a API.");
		}

		let modelForSyonet = detectModelFromPage();
		if (!modelForSyonet || modelForSyonet === "Não informado") {
			modelForSyonet = formData.model || String(submitParams.model || "") || "Não informado";
		}
		const normalizedModel = normalizeModelName(modelForSyonet);

		const syonetComment = resolveSyonetComment(
			normalizedModel,
			submitParams.page
		);

		const syonetPayload: FormPayload = {
			name: formData.name,
			email: formData.email,
			cellPhone: formData.phone,
			registrationNumber: formData.cpf || "",
			consorcio: submitParams.consorcio,
			team: submitParams.team,
			locationId: submitParams.locationId,
			model: modelForSyonet,
			page: submitParams.page,
			wpp: submitParams.wpp,
			dealer: formData.dealer,
			comment: syonetComment,
		};

		const alanOk = await sendToSyonet(syonetPayload, brand, {
			skipToyotaLeadCenter: true,
		});

		if (!alanOk) {
			throw new Error("Erro ao enviar os dados para a API.");
		}

		pushLeadForm();

		if (submitButton) {
			submitButton.disabled = false;
			submitButton.style.opacity = "1";
			submitButton.style.cursor = "pointer";
		}

		return true;
	} catch (error) {
		console.error("Erro ao enviar formulário:", error);
		alert("Erro ao enviar formulário. Por favor, tente novamente.");

		if (submitButton) {
			submitButton.disabled = false;
			submitButton.style.opacity = "1";
			submitButton.style.cursor = "pointer";
		}

		return false;
	}
};

declare global {
	interface Window {
		handlePopupClose: (element: HTMLElement, isWhatsApp: boolean) => void;
	}
}

export default handleSubmit;
